/**
 * Media attached to the post: a scrollable strip of thumbnails with reorder and
 * remove affordances, plus the two ways to add more (library/upload, or AI).
 *
 * Order matters — the first asset is what a single-image platform publishes.
 */

import { ChevronLeft, ChevronRight, Clapperboard, ImagePlus, Sparkles, X } from 'lucide-react';
import type { MediaAsset } from '../../../shared/types';
import { Button, Tooltip } from '../ui';
import { cn } from '../../lib/utils';
import { assetUrl } from './composerUtils';

export interface MediaStripProps {
  assets: MediaAsset[];
  onAdd: () => void;
  onRemove: (assetId: string) => void;
  onMove: (assetId: string, direction: -1 | 1) => void;
  onGenerate?: () => void;
  generating?: boolean;
  disabled?: boolean;
  className?: string;
}

export function MediaStrip({
  assets,
  onAdd,
  onRemove,
  onMove,
  onGenerate,
  generating,
  disabled,
  className,
}: MediaStripProps) {
  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-start gap-2 overflow-x-auto pb-1">
        {assets.map((asset, index) => (
          <div
            key={asset.id}
            className="group relative size-[72px] shrink-0 overflow-hidden rounded-lg border border-border bg-sunken"
          >
            {asset.kind === 'image' ? (
              <img
                src={assetUrl(asset)}
                alt={asset.altText ?? asset.filename}
                loading="lazy"
                className="size-full object-cover"
              />
            ) : (
              <span className="flex size-full items-center justify-center text-subtle">
                <Clapperboard className="size-5" aria-hidden />
              </span>
            )}

            {index === 0 && assets.length > 1 && (
              <span className="absolute left-1 top-1 rounded bg-black/60 px-1 text-[9px] font-semibold uppercase tracking-wide text-white">
                First
              </span>
            )}

            {!disabled && (
              <>
                <button
                  type="button"
                  onClick={() => onRemove(asset.id)}
                  aria-label={`Remove ${asset.filename}`}
                  className="absolute right-1 top-1 flex size-5 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity hover:bg-black/80 focus-visible:opacity-100 group-hover:opacity-100"
                >
                  <X className="size-3" aria-hidden />
                </button>
                {assets.length > 1 && (
                  <div className="absolute inset-x-0 bottom-0 flex justify-between bg-gradient-to-t from-black/70 to-transparent px-0.5 pb-0.5 pt-2 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                    <button
                      type="button"
                      disabled={index === 0}
                      onClick={() => onMove(asset.id, -1)}
                      aria-label={`Move ${asset.filename} earlier`}
                      className="rounded p-0.5 text-white/90 disabled:opacity-30"
                    >
                      <ChevronLeft className="size-3.5" aria-hidden />
                    </button>
                    <button
                      type="button"
                      disabled={index === assets.length - 1}
                      onClick={() => onMove(asset.id, 1)}
                      aria-label={`Move ${asset.filename} later`}
                      className="rounded p-0.5 text-white/90 disabled:opacity-30"
                    >
                      <ChevronRight className="size-3.5" aria-hidden />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        ))}

        <button
          type="button"
          onClick={onAdd}
          disabled={disabled}
          className={cn(
            'flex size-[72px] shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border',
            'text-subtle transition-colors hover:border-border-strong hover:bg-elevated/50 hover:text-fg',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        >
          <ImagePlus className="size-4" aria-hidden />
          <span className="text-[10px] font-medium">Add</span>
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <p className="text-xs text-subtle">
          {assets.length === 0
            ? 'Optional — drafts are stronger when the model can see what you are showing.'
            : `${assets.length} attached · each platform can use its own selection.`}
        </p>
        {onGenerate && (
          <Tooltip content="Generate an on-brand image from your notes">
            <Button
              variant="link"
              size="xs"
              onClick={onGenerate}
              loading={generating}
              disabled={disabled}
              leftIcon={<Sparkles className="size-3.5" />}
            >
              Generate an image
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
